import React from 'react';
import { Link } from 'gatsby';
import usePageContext from '@hooks/usePageContext';
import styles from './Breadcrumbs.module.scss';
import { capitalize, isPlainObject, isString } from 'lodash';

export default function Breadcrumbs({ crumbs, currentPageLabel, ignore }) {
  const {
    breadcrumb,
    isPaginated,
    location,
    ignoreBreadcrumb
  } = usePageContext();

  crumbs = crumbs ?? breadcrumb?.crumbs;

  if (!crumbs?.length) {
    return null;
  }

  const items = crumbs
    .map(({ crumbLabel, pathname }, index) => {
      if (currentPageLabel && index === crumbs.length - 1) {
        crumbLabel = currentPageLabel;
      }

      // Don't show breadcrumb if no label is present
      // or if the current page is paginated (ie. /lab-updates/2)
      if (!crumbLabel || (isPaginated && pathname === location.pathname)) {
        return null;
      }

      crumbLabel = crumbLabel.trim();

      // Allow specific breadcrumbs to be ignored by either
      // passing a prop or with the page's context
      if (ignore || ignoreBreadcrumb) {
        const ignoreArr = [ignore, ignoreBreadcrumb].flat().filter(i => i);
        const shouldIgnore = !!ignoreArr.find(item => {
          if (isString(item) && item === crumbLabel) {
            return true;
          }

          if (isPlainObject(item)) {
            const ignorePath = item.to ?? item.path ?? item.pathname;
            const ignoreLabel = item.label ?? item.crumbLabel;

            if (ignorePath) {
              const regex = isString(ignorePath)
                ? new RegExp(`${ignorePath}/?`)
                : new RegExp(ignorePath);
              return pathname.match(regex);
            }

            if (ignoreLabel) {
              const regex = isString(ignoreLabel)
                ? new RegExp(`^${ignoreLabel.trim()}$`)
                : new RegExp(ignoreLabel);
              return crumbLabel.match(regex);
            }
          }

          return false;
        });

        if (shouldIgnore) {
          return null;
        }
      }

      crumbLabel = crumbLabel
        .replace(/-/g, ' ')
        .split(' ')
        .map(capitalize)
        .join(' ');

      if (pathname) {
        return <Link to={pathname}>{crumbLabel}</Link>;
      } else {
        return <span>{crumbLabel}</span>;
      }
    })
    .filter(i => i);

  return (
    <nav
      className={styles.wrapper}
      aria-label="Breadcrumbs"
    >
      <ol className={styles.breadcrumbs}>
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ol>
    </nav>
  );
}
